import React from 'react';

type InlineNode = React.ReactNode;

export function parseInline(text: string): InlineNode[] {
  const nodes: InlineNode[] = [];
  let remaining = text;

  const nextToken = (): {
    kind: 'link' | 'code' | 'bold';
    index: number;
    match: RegExpMatchArray;
  } | null => {
    const link = remaining.match(/\[([^\]]+)\]\(([^)]+)\)/);
    const code = remaining.match(/`([^`]+)`/);
    const bold = remaining.match(/\*\*([^*]+)\*\*/);

    const candidates: Array<{
      kind: 'link' | 'code' | 'bold';
      match: RegExpMatchArray | null;
    }> = [
      { kind: 'link', match: link },
      { kind: 'code', match: code },
      { kind: 'bold', match: bold },
    ];

    const withIndex = candidates
      .filter((c): c is { kind: 'link' | 'code' | 'bold'; match: RegExpMatchArray } => !!c.match)
      .map((c) => ({
        kind: c.kind,
        match: c.match,
        index: c.match.index ?? -1,
      }))
      .filter((c) => c.index >= 0)
      .sort((a, b) => a.index - b.index);

    return withIndex[0] ?? null;
  };

  while (remaining.length > 0) {
    const token = nextToken();
    if (!token) {
      nodes.push(remaining);
      break;
    }

    if (token.index > 0) {
      nodes.push(remaining.slice(0, token.index));
    }

    const full = token.match[0];
    const key = `${token.kind}-${nodes.length}-${token.index}`;

    if (token.kind === 'code') {
      const content = token.match[1] ?? '';
      nodes.push(
        <code
          key={key}
          className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[0.875em] text-gray-800 dark:bg-gray-800 dark:text-gray-100"
        >
          {content}
        </code>
      );
    } else if (token.kind === 'bold') {
      const content = token.match[1] ?? '';
      nodes.push(<strong key={key}>{content}</strong>);
    } else {
      const label = token.match[1] ?? '';
      const href = token.match[2] ?? '#';
      nodes.push(
        <a
          key={key}
          href={href}
          className="text-primary-600 hover:text-primary-700 dark:text-primary-300 dark:hover:text-primary-200 underline underline-offset-4"
          target={href.startsWith('http') ? '_blank' : undefined}
          rel={href.startsWith('http') ? 'noreferrer noopener' : undefined}
        >
          {label}
        </a>
      );
    }

    remaining = remaining.slice(token.index + full.length);
  }

  return nodes;
}

export function renderMarkdown(markdown: string): React.ReactElement {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const blocks: React.ReactNode[] = [];

  let i = 0;
  const pushParagraph = (textLines: string[]) => {
    const text = textLines.join(' ').trim();
    if (!text) return;
    blocks.push(
      <p key={`p-${blocks.length}`} className="text-gray-700 dark:text-gray-300 leading-relaxed">
        {parseInline(text)}
      </p>
    );
  };

  while (i < lines.length) {
    const line = lines[i] ?? '';
    const trimmed = line.trim();

    // Skip top-level title; we show it in the article header.
    if (trimmed.startsWith('# ')) {
      i += 1;
      continue;
    }

    // Skip tag lines (tag: or tags:)
    if (/^(?:tag|tags):/i.test(trimmed)) {
      i += 1;
      continue;
    }

    // Horizontal rule
    if (trimmed === '---') {
      blocks.push(
        <hr
          key={`hr-${blocks.length}`}
          className="border-gray-200 dark:border-gray-700"
        />
      );
      i += 1;
      continue;
    }

    // Fenced code block
    if (trimmed.startsWith('```')) {
      const fence = trimmed;
      const language = fence.replace(/```/g, '').trim();
      const codeLines: string[] = [];
      i += 1;
      while (i < lines.length && !(lines[i] ?? '').trim().startsWith('```')) {
        codeLines.push(lines[i] ?? '');
        i += 1;
      }
      // Skip closing fence
      if (i < lines.length && (lines[i] ?? '').trim().startsWith('```')) {
        i += 1;
      }

      blocks.push(
        <pre
          key={`code-${blocks.length}`}
          className="overflow-x-auto rounded-lg bg-gray-900 p-4 text-gray-100"
        >
          <code className="text-sm">
            {language ? `// ${language}\n` : ''}
            {codeLines.join('\n')}
          </code>
        </pre>
      );
      continue;
    }

    // Headings (##..######)
    const headingMatch = trimmed.match(/^(#{2,6})\s+(.*)$/);
    if (headingMatch) {
      const level = headingMatch[1]?.length ?? 2;
      const content = headingMatch[2] ?? '';
      const Tag = (`h${Math.min(level, 6)}` as unknown) as keyof JSX.IntrinsicElements;
      const size =
        level === 2
          ? 'text-xl'
          : level === 3
          ? 'text-lg'
          : level === 4
          ? 'text-base'
          : 'text-sm';

      blocks.push(
        <Tag
          key={`h-${blocks.length}`}
          className={`${size} font-semibold text-gray-900 dark:text-gray-100 mt-4`}
        >
          {parseInline(content)}
        </Tag>
      );
      i += 1;
      continue;
    }

    // Unordered list
    if (trimmed.startsWith('- ')) {
      const items: string[] = [];
      while (i < lines.length && (lines[i] ?? '').trim().startsWith('- ')) {
        items.push((lines[i] ?? '').trim().slice(2));
        i += 1;
      }
      blocks.push(
        <ul key={`ul-${blocks.length}`} className="list-disc pl-6 space-y-1 text-gray-700 dark:text-gray-300">
          {items.map((item, idx) => (
            <li key={`ul-${blocks.length}-li-${idx}`}>{parseInline(item)}</li>
          ))}
        </ul>
      );
      continue;
    }

    // Ordered list
    if (/^\d+\.\s+/.test(trimmed)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s+/.test((lines[i] ?? '').trim())) {
        items.push((lines[i] ?? '').trim().replace(/^\d+\.\s+/, ''));
        i += 1;
      }
      blocks.push(
        <ol key={`ol-${blocks.length}`} className="list-decimal pl-6 space-y-1 text-gray-700 dark:text-gray-300">
          {items.map((item, idx) => (
            <li key={`ol-${blocks.length}-li-${idx}`}>{parseInline(item)}</li>
          ))}
        </ol>
      );
      continue;
    }

    // Blank lines
    if (!trimmed) {
      i += 1;
      continue;
    }

    // Paragraph (collect until blank line or special token)
    const paragraphLines: string[] = [];
    while (i < lines.length) {
      const l = lines[i] ?? '';
      const t = l.trim();
      if (
        !t ||
        t === '---' ||
        t.startsWith('```') ||
        t.startsWith('#') ||
        t.startsWith('- ') ||
        /^\d+\.\s+/.test(t) ||
        /^(?:tag|tags):/i.test(t)
      ) {
        break;
      }
      paragraphLines.push(t);
      i += 1;
    }
    pushParagraph(paragraphLines);
  }

  return <div className="space-y-4">{blocks}</div>;
}


